import { ApiError, type Activation, type Feature, type License, type Product } from '@central/api';
import {
  Card,
  StatusChip,
  color,
  downloadText,
  formatAgo,
  formatDate,
  isApiError,
} from '@central/ui';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Table,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useRef, useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { Crumbs, DataTable, Empty, QueryState } from '../components/parts';
import { licenseStatusChip, mono, muted } from '../lib/labels';
import { errorMessage } from '../lib/errors';
import { fromDateInput, toDateInput } from '../lib/format';
import {
  activationsText,
  buildUpdate,
  describeChange,
  describeUpdate,
  draftFrom,
  hasChanges,
  machineIdLines,
  validate,
  type Draft,
  type DraftErrors,
} from '../lib/license';
import { useCanWrite } from '../lib/me';

/** License editor (docs/design/AdminLicense.dc.html). */
export function LicenseEditor() {
  const api = useApi();
  const { licenseId } = useParams({ from: '/app/licenses/$licenseId' });
  const license = useQuery({
    queryKey: keys.license(licenseId),
    queryFn: () => api.licenses.get(licenseId),
  });
  const features = useQuery({
    queryKey: keys.features,
    queryFn: () => api.catalogue.features(),
    staleTime: 300_000,
  });
  const products = useQuery({
    queryKey: keys.products,
    queryFn: () => api.catalogue.products(),
    staleTime: 300_000,
  });
  const l = license.data;

  return (
    <>
      <Crumbs items={[{ label: 'Licenses', to: '/licenses' }, { label: l?.customerName ?? '…' }]} />
      <QueryState
        isPending={license.isPending || features.isPending || products.isPending}
        error={license.error ?? features.error ?? products.error}
      >
        {() => <Body l={l!} features={features.data!.items} products={products.data!.items} />}
      </QueryState>
    </>
  );
}

function Body({ l, features, products }: { l: License; features: Feature[]; products: Product[] }) {
  const st = licenseStatusChip[l.status];
  const canWrite = useCanWrite();
  const [dialog, setDialog] = useState<'offline' | 'suspend'>();
  return (
    <>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Group gap={10} wrap="wrap" justify="space-between">
          <Group gap={10} wrap="wrap" style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                lineHeight: '32px',
                ...mono,
                fontWeight: 500,
                wordBreak: 'break-all',
              }}
            >
              {l.key}
            </h1>
            <StatusChip tone={st.tone}>{st.label}</StatusChip>
          </Group>
          {canWrite && (
            <Group gap={8}>
              <Button variant="default" onClick={() => setDialog('offline')}>
                Offline license file
              </Button>
              <Button
                variant="default"
                color={l.suspended ? undefined : 'red'}
                onClick={() => setDialog('suspend')}
              >
                {l.suspended ? 'Lift suspension' : 'Suspend'}
              </Button>
            </Group>
          )}
        </Group>
        <div style={{ fontSize: 14, ...muted }}>
          <Link to="/customers/$customerId" params={{ customerId: l.customerId }}>
            {l.customerName}
          </Link>{' '}
          · {l.activeActivations} of {activationsText(l.allowedActivations)} activations used · rev{' '}
          {l.rev}
        </div>
        {l.suspended && (
          <Alert
            color="red"
            title={`Suspended${l.suspendedAt ? ` ${formatDate(l.suspendedAt)}` : ''}`}
          >
            Activation and renewal are refused, so its environments enter grace at their next
            renewal. No offline file is issued.
          </Alert>
        )}
      </header>
      <Editor l={l} features={features} products={products} />
      <SuspendModal l={l} opened={dialog === 'suspend'} onClose={() => setDialog(undefined)} />
      <OfflineFileModal l={l} opened={dialog === 'offline'} onClose={() => setDialog(undefined)} />
    </>
  );
}

function SuspendModal({
  l,
  opened,
  onClose,
}: {
  l: License;
  opened: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const suspend = !l.suspended;
  const mutation = useMutation({
    mutationFn: () =>
      api.licenses.update(l.id, { rev: l.rev, reason: reason.trim(), suspended: suspend }),
    onSuccess: async (next) => {
      queryClient.setQueryData(keys.license(l.id), next);
      await queryClient.invalidateQueries({ queryKey: keys.licenseChanges(l.id) });
      close();
    },
  });
  const close = () => {
    setReason('');
    setReasonError(undefined);
    mutation.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setReasonError('Say why; it goes to the audit log.');
    setReasonError(undefined);
    mutation.mutate();
  };
  return (
    <Modal opened={opened} onClose={close} title={suspend ? 'Suspend license' : 'Lift suspension'}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <p style={{ margin: 0, fontSize: 14 }}>
          {suspend
            ? 'Activation and renewal of this license are refused until the suspension is lifted. Its environments enter grace at their next renewal; Save and push to environments makes that happen now, with no other change needed.'
            : 'Its environments can activate and renew this license again.'}
        </p>
        <Textarea
          label="Reason"
          required
          data-autofocus
          value={reason}
          error={reasonError}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {mutation.error && <Alert color="red">{errorMessage(mutation.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" color={suspend ? 'red' : 'dark'} loading={mutation.isPending}>
            {suspend ? 'Suspend' : 'Lift suspension'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

const afterToday = 'Pick a date after today; a file valid until today has already lapsed.';

/**
 * The offline dialog's input errors for a 400's fields. A field the dialog
 * has no input for is left out, so its error stays in the alert.
 */
function offlineFieldErrors(
  fields: Record<string, string> | undefined,
  expiresAt: string,
): { ids?: string; validUntil?: string; reason?: string } {
  const out: { ids?: string; validUntil?: string; reason?: string } = {};
  if (!fields) return out;
  const ids = fields.machineIds;
  if (ids === 'required') out.ids = 'Enter at least one machine ID.';
  else if (ids === 'at most 50') out.ids = 'At most 50 machine IDs at a time.';
  else if (ids?.startsWith('duplicate ')) out.ids = 'A machine ID is listed twice.';
  else if (ids) out.ids = `Machine IDs: ${ids}.`;
  const until = fields.validUntil;
  if (until === 'must be in the future') out.validUntil = afterToday;
  else if (until === 'must not be after the license expires')
    out.validUntil = `No later than the license expiry, ${formatDate(expiresAt)}.`;
  else if (until) out.validUntil = `Valid until: ${until}.`;
  if (fields.reason) out.reason = 'Say why; it goes to the audit log.';
  return out;
}

function OfflineFileModal({
  l,
  opened,
  onClose,
}: {
  l: License;
  opened: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [ids, setIds] = useState('');
  const [validUntil, setValidUntil] = useState(() => toDateInput(l.expiresAt));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ ids?: string; validUntil?: string; reason?: string }>({});
  const issue = useMutation({
    mutationFn: () =>
      api.licenses.issueOffline(l.id, {
        machineIds: machineIdLines(ids),
        validUntil: fromDateInput(validUntil),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setIds('');
      setReason('');
      setErrors({});
      // License, its activations and its counts all live under the license key.
      await queryClient.invalidateQueries({ queryKey: keys.license(l.id) });
    },
    onError: (e) => {
      const f = offlineFieldErrors(e instanceof ApiError ? e.fields : undefined, l.expiresAt);
      if (Object.keys(f).length > 0) setErrors(f);
    },
  });
  const download = useMutation({
    mutationFn: () => api.licenses.offlineFile(l.id),
    onSuccess: (f) => downloadText(f.filename ?? `${l.key}.license`, f.text),
  });
  const close = () => {
    setIds('');
    setReason('');
    setErrors({});
    setValidUntil(toDateInput(l.expiresAt));
    issue.reset();
    download.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: typeof errors = {};
    const lines = machineIdLines(ids);
    if (lines.length === 0) v.ids = 'Enter at least one machine ID.';
    else if (new Set(lines).size !== lines.length) v.ids = 'A machine ID is listed twice.';
    else if (lines.length > 50) v.ids = 'At most 50 machine IDs at a time.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) v.validUntil = 'Enter a date.';
    else if (validUntil <= toDateInput(new Date().toISOString())) v.validUntil = afterToday;
    else if (validUntil > toDateInput(l.expiresAt))
      v.validUntil = `No later than the license expiry, ${formatDate(l.expiresAt)}.`;
    if (!reason.trim()) v.reason = 'Say why; it goes to the audit log.';
    setErrors(v);
    if (Object.keys(v).length === 0) issue.mutate();
  };
  return (
    <Modal opened={opened} onClose={close} title="Offline license file" size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 14 }}>
          One file covers every machine holding an offline activation on this license. GoATMS loads
          it on its License page; it works until the earliest valid-until date of its machines.
        </p>
        <Group>
          <Button variant="default" loading={download.isPending} onClick={() => download.mutate()}>
            Download current file
          </Button>
        </Group>
        {download.error && (
          <Alert color="red">
            {errorMessage(download.error, {
              offline_file_not_found: 'No machine holds an offline activation yet. Add one below.',
            })}
          </Alert>
        )}
        <form
          onSubmit={submit}
          noValidate
          aria-label="Add machines"
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <Textarea
            label="Machine IDs"
            description="One per line, as GoATMS's License page shows them. Each takes a seat."
            autosize
            minRows={3}
            data-autofocus
            styles={{ input: mono }}
            value={ids}
            error={errors.ids}
            onChange={(e) => setIds(e.currentTarget.value)}
          />
          <TextInput
            label="Valid until"
            type="date"
            value={validUntil}
            error={errors.validUntil}
            onChange={(e) => setValidUntil(e.currentTarget.value)}
          />
          <Textarea
            label="Reason (goes to the audit log)"
            required
            value={reason}
            error={errors.reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          {issue.error && Object.keys(errors).length === 0 && (
            <Alert color="red">
              {errorMessage(issue.error, {
                already_active: 'Every machine ID listed already holds an offline activation.',
              })}
            </Alert>
          )}
          {issue.data && (
            <Alert color="green">
              Added {issue.data.items.length} machine{issue.data.items.length === 1 ? '' : 's'}.
              Download the file to include {issue.data.items.length === 1 ? 'it' : 'them'}.
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Close
            </Button>
            <Button type="submit" color="dark" loading={issue.isPending}>
              Add machines
            </Button>
          </Group>
        </form>
      </div>
    </Modal>
  );
}

function Editor({
  l,
  features,
  products,
}: {
  l: License;
  features: Feature[];
  products: Product[];
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(l, products));
  // The license the draft was made from; it trails l when someone else saved.
  const [base, setBase] = useState(l);
  const [reloadRequested, setReloadRequested] = useState(false);
  const [reason, setReason] = useState('');
  const [savedRev, setSavedRev] = useState<number>();
  const [pushed, setPushed] = useState(false);
  // Which save button submitted the form; read synchronously by submit.
  const pushRef = useRef(true);
  const [errorRev, setErrorRev] = useState<number>();
  const [errors, setErrors] = useState<DraftErrors & { reason?: string }>({});

  const save = useMutation({
    mutationFn: (push: boolean) => api.licenses.update(l.id, push ? { ...update, push } : update),
    onSuccess: async (next, push) => {
      setReason('');
      setSavedRev(next.rev);
      setPushed(push);
      if (push) await queryClient.invalidateQueries({ queryKey: keys.licenseActivations(l.id) });
      rebase(next);
      queryClient.setQueryData(keys.license(l.id), next);
      await queryClient.invalidateQueries({ queryKey: keys.licenseChanges(l.id) });
      await queryClient.invalidateQueries({ queryKey: keys.licenseList(l.customerId) });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
    },
    onError: (e) => {
      setErrorRev(l.rev);
      const f = (e as { fields?: Record<string, string> }).fields;
      if (f?.reason) setErrors((x) => ({ ...x, reason: 'Say why; it goes to the audit log.' }));
    },
  });

  function rebase(next: License) {
    setBase(next);
    setDraft(draftFrom(next, products));
    setErrors({});
    setReloadRequested(false);
  }

  // A newer rev restarts the draft from the stored license (the typed reason
  // is kept) unless the draft holds edits the newer license lacks; those stay
  // until the user reloads, and the draft cannot be saved over the newer rev.
  const behind = l.rev !== base.rev;
  const keepsEdits =
    hasChanges(buildUpdate(base, draft, reason)) && hasChanges(buildUpdate(l, draft, reason));
  if (behind && (reloadRequested || !keepsEdits)) rebase(l);
  const changedUnderDraft = behind && !reloadRequested && keepsEdits;

  // A save error belongs to the rev it was made against.
  const saveError = errorRev === l.rev ? save.error : null;

  const update = buildUpdate(l, draft, reason);
  const pending = describeUpdate(l, update, features);
  const changed = hasChanges(update);

  const reload = () => {
    setReloadRequested(true);
    void queryClient.invalidateQueries({ queryKey: keys.license(l.id) });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: DraftErrors & { reason?: string } = validate(draft);
    if (!reason.trim()) v.reason = 'Say why; it goes to the audit log.';
    setErrors(v);
    if (Object.keys(v).length === 0) save.mutate(pushRef.current);
  };

  const set = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  const setFeature = (
    pi: number,
    featureId: string,
    patch: { enabled?: boolean; quantity?: number },
  ) =>
    set({
      products: draft.products.map((p, i) =>
        i !== pi
          ? p
          : {
              ...p,
              features: p.features.map((f) => (f.featureId === featureId ? { ...f, ...patch } : f)),
            },
      ),
    });

  const featureById = new Map(features.map((f) => [f.id, f]));
  // An archived feature stays on where the stored license enables it, and
  // is never newly turned on.
  const storedOn = new Set(
    l.products.flatMap((p) => p.features.filter((f) => f.enabled).map((f) => f.featureId)),
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
      <div
        style={{
          flex: '2 1 480px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Card title="Term">
          <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 14,
              }}
            >
              <TextInput
                label="Starts"
                type="date"
                size="sm"
                value={draft.startsAt}
                error={errors.startsAt}
                onChange={(e) => set({ startsAt: e.currentTarget.value })}
              />
              <TextInput
                label="Expires"
                type="date"
                size="sm"
                value={draft.expiresAt}
                error={errors.expiresAt}
                onChange={(e) => set({ expiresAt: e.currentTarget.value })}
              />
              <TextInput
                label="Grace after expiry (days)"
                inputMode="numeric"
                size="sm"
                value={draft.graceDays}
                error={errors.graceDays}
                onChange={(e) => set({ graceDays: e.currentTarget.value })}
              />
              <TextInput
                label="Allowed activations"
                inputMode="numeric"
                size="sm"
                value={draft.allowedActivations}
                description="−1 unlimited · 0 offline only"
                error={errors.allowedActivations}
                onChange={(e) => set({ allowedActivations: e.currentTarget.value })}
              />
            </div>
          </fieldset>
        </Card>

        {draft.products.map((p, pi) => (
          <Card
            key={p.productId}
            title="Modules & seats"
            action={
              <span style={{ fontSize: 13, ...muted }}>
                Product: {products.find((x) => x.id === p.productId)?.name ?? p.productId}
              </span>
            }
            padded={false}
          >
            {errors.products && (
              <Alert color="red" mx={20} mb={12}>
                {errors.products}
              </Alert>
            )}
            {p.features.map((f) => {
              const meta = featureById.get(f.featureId);
              const name = meta?.name ?? f.featureId;
              const locked = !!meta?.archived && !storedOn.has(f.featureId);
              return (
                <div
                  key={f.featureId}
                  style={{
                    display: 'flex',
                    gap: 14,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    padding: '10px 20px',
                    borderTop: `1px solid ${color.rowRule}`,
                    minHeight: 44,
                  }}
                >
                  <Checkbox
                    aria-label={name}
                    checked={f.enabled}
                    disabled={!canWrite || (locked && !f.enabled)}
                    onChange={(e) =>
                      setFeature(pi, f.featureId, { enabled: e.currentTarget.checked })
                    }
                  />
                  <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {name}
                      {meta?.archived && (
                        <>
                          {' '}
                          <StatusChip tone="unk">Archived</StatusChip>
                        </>
                      )}
                    </span>
                    <span style={{ ...mono, fontSize: 12, ...muted }}>{meta?.key ?? ''}</span>
                    {locked && (
                      <span style={{ fontSize: 12, ...muted }}>
                        Archived features cannot be newly turned on.
                      </span>
                    )}
                  </div>
                  {meta?.isQuantity && (
                    <Group gap={10} wrap="nowrap">
                      {l.seatsUsed?.[f.featureId] !== undefined && (
                        <span style={{ fontSize: 13, ...muted, ...mono }}>
                          {l.seatsUsed[f.featureId]} used of
                        </span>
                      )}
                      <NumberInput
                        aria-label={`${name} seats`}
                        size="sm"
                        w={110}
                        min={0}
                        allowDecimal={false}
                        allowNegative={false}
                        hideControls
                        disabled={!canWrite}
                        value={f.quantity}
                        styles={{ input: { ...mono, textAlign: 'right' } }}
                        onChange={(v) =>
                          setFeature(pi, f.featureId, { quantity: typeof v === 'number' ? v : 0 })
                        }
                      />
                      <span style={{ fontSize: 13, width: 56, ...muted }}>{meta.unit ?? ''}</span>
                    </Group>
                  )}
                </div>
              );
            })}
          </Card>
        ))}

        <Activations licenseId={l.id} />
      </div>

      <div
        style={{
          flex: '1 1 280px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Card title="Save changes">
          <form
            onSubmit={submit}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            {changedUnderDraft || isApiError(saveError, 'stale_rev') ? (
              <Alert color="yellow" title="This license changed">
                Someone saved this license after you opened it. Reload to see their change, then
                make yours again.
                <Button mt={10} size="xs" variant="default" onClick={reload}>
                  Reload latest
                </Button>
              </Alert>
            ) : (
              saveError && !errors.reason && <Alert color="red">{errorMessage(saveError)}</Alert>
            )}
            {savedRev === l.rev && !changed && (
              <Alert color="green">
                {pushed
                  ? 'Saved and pushed. Environments holding this license renew at their next heartbeat.'
                  : 'Saved. Environments pick this up at their next renewal.'}
              </Alert>
            )}
            <Textarea
              label="Reason (goes to the audit log)"
              required
              autosize
              minRows={3}
              disabled={!canWrite}
              value={reason}
              error={errors.reason}
              onChange={(e) => {
                setReason(e.currentTarget.value);
                setErrors((x) => ({ ...x, reason: undefined }));
              }}
            />
            {changed && (
              <div
                aria-label="Pending changes"
                style={{
                  fontSize: 13,
                  lineHeight: '19px',
                  color: color.textBody,
                  background: color.page,
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                {pending.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div style={{ marginTop: 6, ...muted }}>
                  Environments pick this up at their next renewal (within 24 h), or push now.
                </div>
              </div>
            )}
            <Button
              type="submit"
              color="dark"
              loading={save.isPending && save.variables}
              disabled={!canWrite || changedUnderDraft || (save.isPending && !save.variables)}
              onClick={() => (pushRef.current = true)}
            >
              Save and push to environments
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={save.isPending && !save.variables}
              disabled={
                !canWrite || !changed || changedUnderDraft || (save.isPending && save.variables)
              }
              onClick={() => (pushRef.current = false)}
            >
              Save only
            </Button>
          </form>
        </Card>
        <History licenseId={l.id} features={features} />
      </div>
    </div>
  );
}

function History({ licenseId, features }: { licenseId: string; features: Feature[] }) {
  const api = useApi();
  const changes = useQuery({
    queryKey: keys.licenseChanges(licenseId),
    queryFn: () => api.licenses.changes(licenseId),
  });
  return (
    <Card
      title="History"
      action={
        <Link to="/audit" search={{ target: `licenses/${licenseId}` }} style={{ fontSize: 13 }}>
          Audit history
        </Link>
      }
    >
      <QueryState isPending={changes.isPending} error={changes.error}>
        {() =>
          changes.data!.items.length === 0 ? (
            <span style={{ fontSize: 13, ...muted }}>No changes yet.</span>
          ) : (
            <ol
              aria-label="History"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {changes.data!.items.map((c) => (
                <li
                  key={c.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 13,
                    paddingBottom: 10,
                    borderBottom: `1px solid ${color.rowRule}`,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {c.diff.length
                      ? c.diff.map((d) => describeChange(d, features)).join(' · ')
                      : 'No field changes'}
                  </span>
                  <span style={muted}>
                    {c.by.email ?? c.by.kind} · {formatDate(c.at)} · {c.reason}
                  </span>
                </li>
              ))}
            </ol>
          )
        }
      </QueryState>
    </Card>
  );
}

function Activations({ licenseId }: { licenseId: string }) {
  const api = useApi();
  const canWrite = useCanWrite();
  const activations = useQuery({
    queryKey: keys.licenseActivations(licenseId),
    queryFn: () => api.licenses.activations(licenseId),
  });
  const [acting, setActing] = useState<{ kind: ActivationAction; activation: Activation }>();
  return (
    <Card title="Activations" padded={false}>
      <QueryState isPending={activations.isPending} error={activations.error}>
        {() =>
          activations.data!.items.length === 0 ? (
            <Empty>No activations.</Empty>
          ) : (
            <DataTable
              label="Activations"
              head={['Environment', 'Type', 'Machine ID', 'Last renewed', 'Renew due', '']}
              minWidth={720}
            >
              {activations.data!.items.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td style={{ fontWeight: 600 }}>
                    {a.environmentId ? (
                      <Link
                        to="/environments/$environmentId"
                        params={{ environmentId: a.environmentId }}
                      >
                        {a.environmentName ?? a.environmentId}
                      </Link>
                    ) : (
                      '–'
                    )}
                    {a.tenantId && (
                      <span style={{ ...muted, fontWeight: 400 }}> · {a.tenantId}</span>
                    )}
                    {a.legacy && (
                      <>
                        {' '}
                        <StatusChip tone="unk">Legacy</StatusChip>
                      </>
                    )}
                  </Table.Td>
                  <Table.Td>{a.type === 'online' ? 'Online' : 'Offline'}</Table.Td>
                  <Table.Td style={mono}>{a.machineIds.map(shortId).join(', ') || '–'}</Table.Td>
                  <Table.Td>{formatAgo(a.lastRenewedAt)}</Table.Td>
                  <Table.Td>
                    {formatDate(a.renewalDueAt)}
                    {a.renewRequestedAt && a.active && (
                      <>
                        {' '}
                        <StatusChip tone="warn">Push pending</StatusChip>
                      </>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {a.revokedAt ? (
                      <Group gap="xs" wrap="nowrap">
                        <span style={muted}>Revoked {formatDate(a.revokedAt)}</span>
                        {canWrite && (
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => setActing({ kind: 'reinstate', activation: a })}
                          >
                            Reinstate
                          </Button>
                        )}
                      </Group>
                    ) : !a.active ? (
                      <span style={muted}>Inactive</span>
                    ) : (
                      canWrite && (
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          onClick={() => setActing({ kind: 'revoke', activation: a })}
                        >
                          Revoke
                        </Button>
                      )
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      <ActivationActionModal
        licenseId={licenseId}
        kind={acting?.kind}
        activation={acting?.activation}
        onClose={() => setActing(undefined)}
      />
    </Card>
  );
}

function shortId(id: string) {
  return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

type ActivationAction = 'revoke' | 'reinstate';

const activationActions: Record<
  ActivationAction,
  { title: string; verb: string; color?: string; effect: string }
> = {
  revoke: {
    title: 'Revoke activation',
    verb: 'Revoke',
    color: 'red',
    effect: 'loses its license at its next renewal and enters grace.',
  },
  reinstate: {
    title: 'Reinstate activation',
    verb: 'Reinstate',
    effect: 'may activate this license again. Its next activation takes a seat if one is free.',
  },
};

function ActivationActionModal({
  licenseId,
  kind,
  activation,
  onClose,
}: {
  licenseId: string;
  kind?: ActivationAction;
  activation?: Activation;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const action = activationActions[kind ?? 'revoke'];
  const mutation = useMutation({
    mutationFn: ({ k, a }: { k: ActivationAction; a: Activation }) =>
      api.activations[k](a.id, { reason: reason.trim() }),
    onSuccess: async () => {
      // License, its activations and its counts all live under the license key.
      await queryClient.invalidateQueries({ queryKey: keys.license(licenseId) });
      close();
    },
  });
  const close = () => {
    setReason('');
    setReasonError(undefined);
    mutation.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setReasonError('Say why; it goes to the audit log.');
    setReasonError(undefined);
    if (activation && kind) mutation.mutate({ k: kind, a: activation });
  };
  return (
    <Modal opened={!!activation} onClose={close} title={action.title}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <p style={{ margin: 0, fontSize: 14 }}>
          {activation?.environmentName ?? 'This installation'} {action.effect}
        </p>
        <Textarea
          label="Reason"
          required
          data-autofocus
          value={reason}
          error={reasonError}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        {mutation.error && <Alert color="red">{errorMessage(mutation.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" color={action.color} loading={mutation.isPending}>
            {action.verb}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
